import { apiClient } from "./client";

export interface PlotMatrixStage {
  stageName: string;
  stageCode: string;
  total: number;
}

export interface PlotMatrixSubmissionItem {
  "PLOT NO": string;
  "HOUSE TYPE": string;
  "SUB TOTAL": string | number;
  Discount: string | number;
  TOTAL: string | number;
  stages: PlotMatrixStage[];
}

export interface PlotMatrixSubmissionRequest {
  projectId?: string;
  projectName?: string;
  submittedBy?: string;
  submittedAt?: string;
  plots: PlotMatrixSubmissionItem[];
}

export interface PlotMatrixSubmissionResponse {
  success: boolean;
  data?: any;
  message?: string;
  submissionId?: string;
}

/**
 * Format plot matrix data for API submission. `groundworksData` is accepted
 * for call-site compatibility (the central-repo payload builder in
 * `GroundworksReview.tsx` passes it through) but is no longer read here —
 * each stage's `total` is taken directly from the Plot Matrix cell value
 * (`cellData?.total`), and the per-stage cost breakdown has been removed
 * from the payload.
 */
export const formatPlotMatrixForSubmission = (
  plotMatrixData: any[],
  dynamicColumns: any[],
  projectInfo?: {
    projectId?: string;
    projectName?: string;
    submittedBy?: string;
  },
  _groundworksData?: any,
): PlotMatrixSubmissionRequest => {
  const plots: PlotMatrixSubmissionItem[] = plotMatrixData.map((plot) => {
    // Calculate total from all categories
    let plotTotal = 0;

    // Format plotNo with P-/G- prefix to match foundations convention
    const rawPlotNo = String(plot.plotNo || "");
    const plotPrefix = plot.rowType === 'garage' ? 'G-' : 'P-';
    const formattedPlotNo = rawPlotNo.startsWith('P-') || rawPlotNo.startsWith('G-')
      ? rawPlotNo
      : rawPlotNo ? `${plotPrefix}${rawPlotNo}` : '';

    // Create plot submission object with basic info only
    const plotSubmission: any = {
      "PLOT NO": formattedPlotNo,
      "HOUSE TYPE": plot.housetype || "",
    };

    // Build one stage per Plot Matrix column. `stage.total` is the cell
    // value (literal `Σ_fields Math.abs(rowQty × rate)` after the recent
    // cell-calc refactor) — the same number the user sees on the matrix.
    const stages: any[] = [];

    dynamicColumns.forEach((col) => {
      const cellData = plot[col.dataIndex];
      const categoryTotal = cellData?.total || 0;
      const fieldName = col.categoryName || col.stepName;

      stages.push({
        stageName: fieldName,
        stageCode: col.dataIndex || fieldName,
        total: categoryTotal,
      });

      plotTotal += categoryTotal;
    });

    // Calculate discount (example: 3% - adjust as needed)
    const discountPercentage = 0.03;
    const discount = plotTotal * discountPercentage;
    const finalTotal = plotTotal - discount;

    // Add totals
    plotSubmission["SUB TOTAL"] = plotTotal;
    plotSubmission["Discount"] = -discount;
    plotSubmission["TOTAL"] = finalTotal;

    // Add stages array (no top-level costBreakdown, only in stages)
    plotSubmission["stages"] = stages;

    return plotSubmission as PlotMatrixSubmissionItem;
  });

  return {
    projectId: projectInfo?.projectId,
    projectName: projectInfo?.projectName,
    submittedBy: projectInfo?.submittedBy,
    submittedAt: new Date().toISOString(),
    plots,
  };
};

export const plotMatrixApi = {
  /**
   * Submit plot matrix for review
   */
  submitForReview: async (
    data: PlotMatrixSubmissionRequest
  ): Promise<PlotMatrixSubmissionResponse> => {
    try {
      const response = await apiClient.post("/v3/plotsubmissions", data);

      return {
        success: true,
        data: response.data,
        message: "Plot matrix submitted successfully",
        submissionId: response.data?.id,
      };
    } catch (error: any) {
      console.error("=== PLOT MATRIX API CALL ERROR ===");
      console.error("Error Type:", error.constructor.name);
      console.error("Error Message:", error.message);
      console.error("Error Response:", error.response);
      console.error("Error Response Status:", error.response?.status);
      console.error("Error Response Data:", error.response?.data);
      console.error("Error Config:", error.config);
      console.error("Full Error:", error);

      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to submit plot matrix",
      };
    }
  },

  /**
   * Get submission status
   */
  getSubmissionStatus: async (
    submissionId: string
  ): Promise<PlotMatrixSubmissionResponse> => {
    try {
      const response = await apiClient.get(
        `/plotmatrix/submission/${submissionId}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error("Get Submission Status Error:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to get submission status",
      };
    }
  },

  /**
   * Get all submissions for a project
   */
  getProjectSubmissions: async (
    projectId: string
  ): Promise<PlotMatrixSubmissionResponse> => {
    try {
      const response = await apiClient.get(
        `/plotmatrix/project/${projectId}/submissions`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error("Get Project Submissions Error:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to get project submissions",
      };
    }
  },

  /**
   * Get plot submission by project name
   */
  getPlotSubmissionByProjectName: async (
    projectName: string
  ): Promise<PlotMatrixSubmissionResponse> => {
    try {
      const response = await apiClient.get(
        `/v3/plotsubmissions/project/${encodeURIComponent(projectName)}`
      );

      return {
        success: true,
        data: response.data,
        message: "Plot submission found",
      };
    } catch (error: any) {
      console.error("=== GET PLOT SUBMISSION ERROR ===");
      console.error("Error Type:", error.constructor.name);
      console.error("Error Message:", error.message);
      console.error("Error Response:", error.response);
      console.error("Error Response Status:", error.response?.status);
      console.error("Error Response Data:", error.response?.data);

      // 404 means no submission exists yet - this is not an error
      if (error.response?.status === 404) {
        return {
          success: false,
          data: null,
          message: "No submission found",
        };
      }

      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to get plot submission",
      };
    }
  },

  /**
   * Update existing plot submission
   */
  updatePlotSubmission: async (
    projectName: string,
    data: PlotMatrixSubmissionRequest
  ): Promise<PlotMatrixSubmissionResponse> => {
    try {
      const response = await apiClient.put(
        `/v3/plotsubmissions/project/${encodeURIComponent(projectName)}`,
        data
      );

      return {
        success: true,
        data: response.data,
        message: "Plot matrix updated successfully",
        submissionId: response.data?.id,
      };
    } catch (error: any) {
      console.error("=== UPDATE PLOT SUBMISSION ERROR ===");
      console.error("Error Type:", error.constructor.name);
      console.error("Error Message:", error.message);
      console.error("Error Response:", error.response);
      console.error("Error Response Status:", error.response?.status);
      console.error("Error Response Data:", error.response?.data);
      console.error("Error Config:", error.config);
      console.error("Full Error:", error);

      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to update plot matrix",
      };
    }
  },
};
